import React, { useState, useEffect } from 'react';
import { HelpCircle, BookOpen, MessageCircle, Mail, Phone, Clock, CheckCircle, BarChart3, Shield, Zap, AlertTriangle, Eye, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/index';

const helpSections = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'campaigns', label: 'Campaigns', icon: MessageCircle },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'integrations', label: 'Integrations', icon: Zap },
  { id: 'support', label: 'Support', icon: HelpCircle },
];

const cyclingTexts = [
  'How can we help you today?',
  'Find answers, tips, and best practices',
  'Get support for SMS & Voicemail',
  'Learn to optimize your campaigns',
  'Explore analytics and reporting',
  'Stay compliant with regulations',
  'Integrate with your favorite tools',
];

const HelpPage: React.FC = () => {
  const [cyclingIndex, setCyclingIndex] = useState(0);
  const [selectedSection, setSelectedSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setCyclingIndex((prev) => (prev + 1) % cyclingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex">
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 bg-neutral-900/80 border-r border-neutral-800/60 min-h-screen p-6 hidden md:block">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
          <HelpCircle className="w-5 h-5 mr-2 text-blue-400" /> Help Center
        </h2>
        <nav className="space-y-2">
          {helpSections.map(section => (
            <button
              key={section.id}
              className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors text-left text-white hover:bg-neutral-800/60 ${selectedSection === section.id ? 'bg-blue-500/20 text-blue-400' : 'text-neutral-300'}`}
              onClick={() => setSelectedSection(section.id)}
            >
              <section.icon className="w-5 h-5 mr-3" />
              {section.label}
            </button>
          ))}
        </nav>
      </div>
      {/* Main Content */}
      <div className="flex-1 min-w-0 p-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <h1 className="text-5xl font-bold text-white mb-4">Help & Support</h1>
          <div className="text-3xl font-semibold h-12 flex items-center justify-center mb-2">
            <motion.span
              key={cyclingIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent"
            >
              {cyclingTexts[cyclingIndex]}
            </motion.span>
          </div>
          <p className="text-lg text-neutral-400 mb-6">Find answers, get support, and explore resources for Bombard's SMS and voicemail platform.</p>
          <div className="max-w-2xl mx-auto mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search help topics, e.g. 'How to send SMS'"
              className="w-full px-6 py-4 text-lg border border-neutral-700 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-neutral-900 text-white shadow-lg"
            />
          </div>
        </div>
        {/* Section Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          {/* Example interactive cards for each section */}
          {selectedSection === 'getting-started' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><BookOpen className="w-5 h-5 mr-2 text-blue-400" /> Getting Started</h3>
                  <p className="text-neutral-300 mb-4">Learn how to set up your first campaign, import contacts, and start reaching customers.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Account setup & onboarding</li>
                    <li>Importing your first list</li>
                    <li>Launching your first campaign</li>
                  </ul>
                </div>
                <Button variant="primary" onClick={() => setSelectedSection('campaigns')}>Go to Campaigns</Button>
              </motion.div>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><HelpCircle className="w-5 h-5 mr-2 text-blue-400" /> Need Help?</h3>
                  <p className="text-neutral-300 mb-4">Check out our quick start guide or contact support for personalized assistance.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Step-by-step guides</li>
                    <li>Video tutorials</li>
                    <li>Live chat support</li>
                  </ul>
                </div>
                <Button variant="outline" onClick={() => setSelectedSection('support')}>Contact Support</Button>
              </motion.div>
            </>
          )}
          {selectedSection === 'campaigns' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><MessageCircle className="w-5 h-5 mr-2 text-blue-400" /> Campaigns</h3>
                  <p className="text-neutral-300 mb-4">Manage your SMS and voicemail campaigns, track performance, and optimize results.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Creating and scheduling campaigns</li>
                    <li>Personalization & compliance</li>
                    <li>Monitoring delivery & replies</li>
                  </ul>
                </div>
                <Button variant="primary" onClick={() => setSelectedSection('analytics')}>Go to Analytics</Button>
              </motion.div>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><Zap className="w-5 h-5 mr-2 text-blue-400" /> Automation</h3>
                  <p className="text-neutral-300 mb-4">Automate your outreach with advanced triggers, sequences, and integrations.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Setting up sequences</li>
                    <li>Integrating with CRM</li>
                    <li>Using AI for optimization</li>
                  </ul>
                </div>
                <Button variant="outline" onClick={() => setSelectedSection('integrations')}>Explore Integrations</Button>
              </motion.div>
            </>
          )}
          {selectedSection === 'analytics' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><BarChart3 className="w-5 h-5 mr-2 text-blue-400" /> Analytics</h3>
                  <p className="text-neutral-300 mb-4">Understand your campaign performance, delivery rates, and engagement metrics.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Viewing analytics dashboards</li>
                    <li>Exporting reports</li>
                    <li>Customizing analytics views</li>
                  </ul>
                </div>
                <Button variant="primary" onClick={() => setSelectedSection('compliance')}>Go to Compliance</Button>
              </motion.div>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><Eye className="w-5 h-5 mr-2 text-blue-400" /> Engagement Insights</h3>
                  <p className="text-neutral-300 mb-4">Leverage AI to predict engagement and optimize your messaging strategy.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Engagement scoring</li>
                    <li>Reply tracking</li>
                    <li>AI-powered suggestions</li>
                  </ul>
                </div>
                <Button variant="outline" onClick={() => setSelectedSection('support')}>Get Support</Button>
              </motion.div>
            </>
          )}
          {selectedSection === 'compliance' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><Shield className="w-5 h-5 mr-2 text-blue-400" /> Compliance</h3>
                  <p className="text-neutral-300 mb-4">Stay compliant with TCPA, opt-out management, and best practices for outreach.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Opt-out management</li>
                    <li>Compliance dashboards</li>
                    <li>Regulatory resources</li>
                  </ul>
                </div>
                <Button variant="primary" onClick={() => setSelectedSection('support')}>Contact Compliance Team</Button>
              </motion.div>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><AlertTriangle className="w-5 h-5 mr-2 text-yellow-400" /> Best Practices</h3>
                  <p className="text-neutral-300 mb-4">Review our compliance checklist and avoid common pitfalls.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>Consent management</li>
                    <li>Message frequency</li>
                    <li>Data privacy</li>
                  </ul>
                </div>
                <Button variant="outline" onClick={() => setSelectedSection('support')}>Get Compliance Help</Button>
              </motion.div>
            </>
          )}
          {selectedSection === 'integrations' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><Zap className="w-5 h-5 mr-2 text-blue-400" /> Integrations</h3>
                  <p className="text-neutral-300 mb-4">Connect Bombard with your CRM, email, and other tools for seamless workflows.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>CRM integrations</li>
                    <li>Email & calendar sync</li>
                    <li>Webhooks & APIs</li>
                  </ul>
                </div>
                <Button variant="primary" onClick={() => setSelectedSection('support')}>Integration Support</Button>
              </motion.div>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><Settings className="w-5 h-5 mr-2 text-blue-400" /> API & Webhooks</h3>
                  <p className="text-neutral-300 mb-4">Explore our API documentation and learn how to automate your workflows.</p>
                  <ul className="list-disc list-inside text-neutral-400 text-sm space-y-1 mb-4">
                    <li>API reference</li>
                    <li>Webhook setup</li>
                    <li>Developer resources</li>
                  </ul>
                </div>
                <Button variant="outline" onClick={() => setSelectedSection('support')}>API Help</Button>
              </motion.div>
            </>
          )}
          {selectedSection === 'support' && (
            <>
              <motion.div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-8 flex flex-col justify-between shadow-lg col-span-2">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-2 flex items-center"><HelpCircle className="w-5 h-5 mr-2 text-blue-400" /> Contact Support</h3>
                  <p className="text-neutral-300 mb-4">Need help? Reach out to our support team or explore our resources below.</p>
                  <div className="flex flex-col md:flex-row gap-6 mb-6">
                    <div className="flex-1 bg-neutral-900/80 border border-neutral-800 rounded-lg p-6 flex items-center">
                      <Mail className="w-6 h-6 text-blue-400 mr-4" />
                      <div>
                        <div className="text-white font-medium">Email Support</div>
                        <div className="text-neutral-400 text-sm">support@bombard.com</div>
                      </div>
                    </div>
                    <div className="flex-1 bg-neutral-900/80 border border-neutral-800 rounded-lg p-6 flex items-center">
                      <Phone className="w-6 h-6 text-blue-400 mr-4" />
                      <div>
                        <div className="text-white font-medium">Call Us</div>
                        <div className="text-neutral-400 text-sm">+1 (800) 123-4567</div>
                      </div>
                    </div>
                    <div className="flex-1 bg-neutral-900/80 border border-neutral-800 rounded-lg p-6 flex items-center">
                      <MessageCircle className="w-6 h-6 text-blue-400 mr-4" />
                      <div>
                        <div className="text-white font-medium">Live Chat</div>
                        <div className="text-neutral-400 text-sm">Chat with us now</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center mt-4">
                    <Button variant="primary" size="lg">Open Support Ticket</Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpPage; 